/**
 * VerifiedBadge Component
 * Shield + checkmark icon with tooltip showing membership tier label.
 * Source of truth for verified badge rendering across the app.
 */

import { ShieldCheck } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { LCommon } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { cn } from '@/lib/utils';

interface VerifiedBadgeProps {
  /** Icon size in px (default 16) */
  size?: number;
  className?: string;
  /** Expert display name for the tooltip */
  displayName: string;
  /** Membership tier value from DB */
  membershipTier: string;
}

/** Map tier to human-readable label */
const TIER_LABELS: Record<string, string> = {
  featured: 'Verified Expert',
  expert: 'Expert',
  professional: 'Professional',
  business: 'Business',
};

function getTierLabel(tier: string): string {
  return TIER_LABELS[tier.toLowerCase()] ?? 'Verified Expert';
}

export function VerifiedBadge({
  size = 16,
  className,
  displayName,
  membershipTier,
}: VerifiedBadgeProps) {
  const { lCommon } = useLocalizations();
  const tierLabel = getTierLabel(membershipTier);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <ShieldCheck
            className={cn('text-primary flex-shrink-0', className)}
            size={size}
            strokeWidth={2.5}
            aria-label={lCommon('verified_badge.aria_label', { defaultValue: 'Verified' })}
          />
        </TooltipTrigger>
        <TooltipContent side="top">
          <LCommon
            id="verified_badge.tooltip"
            defaultValue={`${displayName} is a ${tierLabel}`}
            values={{ name: displayName, tier: tierLabel }}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
